import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo } from "react";
import styled from "styled-components";

import type { Groups } from "@/components/BacklogTable/hooks";
import { ItemsTable } from "@/components/BacklogTable/ItemsTable";
import type { Item } from "@/types/common";

interface CloneItemListProps {
  items: Item[];
  selectedItems: string[];
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
}
export function CloneItemList({ items, selectedItems, setSelectedItems }: CloneItemListProps) {
  const onSelectChange = useCallback(
    ({ selected, unselected }: { selected: string[]; unselected: string[] }) => {
      return setSelectedItems((current) =>
        [...new Set([...current, ...selected])].filter((id) => !unselected.includes(id))
      );
    },
    [setSelectedItems]
  );
  const groups = items.reduce<Groups>((groups: Groups, item: Item) => {
    const id = item.group.id;
    if (!groups[id]) {
      return { ...groups, [id]: { ...item.group, items: [item] } };
    } else {
      groups[id].items.push(item);
      return groups;
    }
  }, {});
  const selectedItemIds = useMemo(
    () =>
      selectedItems.reduce<{ [key: string]: boolean }>((ids, key) => {
        ids[key] = true;
        return ids;
      }, {}),
    [JSON.stringify(selectedItems)]
  );
  return (
    <GroupsWrapper id="groups">
      {Object.entries(groups).map(
        ([id, group]) =>
          group.items && (
            <ItemsTable
              key={id}
              selectedItemIds={selectedItemIds}
              isRowSelectionEnabled={true}
              items={group.items}
              group={group}
              onSelectChange={onSelectChange}
            />
          )
      )}
    </GroupsWrapper>
  );
}

const GroupsWrapper = styled.div`
  overflow: auto;
  width: 100%;
  font-family: var(--font-family);
`;
